'use client';

import { useMemo, useState } from 'react';

import Image from 'next/image';

import { cn, Input } from '@holistic/components';
import {
  ArrowRight,
  Check,
  ImageOff,
  Search,
} from '@holistic/components/icons';

import type { TalepOption } from './types';

import { SEARCH_THRESHOLD } from './constants';

type OptionGridProps = {
  options: TalepOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Verilirse listenin üstünde arama kutusu çıkar (marka/model adımları). */
  searchPlaceholder?: string;
};

const OptionGrid = ({
  options,
  selectedId,
  onSelect,
  searchPlaceholder,
}: OptionGridProps) => {
  const [term, setTerm] = useState('');
  const showSearch = !!searchPlaceholder && options.length >= SEARCH_THRESHOLD;

  const visible = useMemo(() => {
    const q = term.trim().toLocaleLowerCase('tr');
    if (!q) return options;
    return options.filter((o) =>
      `${o.label} ${o.hint ?? ''}`.toLocaleLowerCase('tr').includes(q)
    );
  }, [options, term]);

  return (
    <div className="space-y-4">
      {showSearch && (
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-11 rounded-xl pl-10"
          />
        </div>
      )}

      {visible.length === 0 ? (
        <p className="rounded-2xl border border-gray-100 bg-gray-50/60 p-5 text-base text-gray-600">
          Aramanızla eşleşen bir sonuç yok.
        </p>
      ) : (
        /* Arama kutusu dışarıda kalıyor: liste kayarken de erişilebilir
           olmalı. Radix ScrollArea yerine yerel kaydırma: viewport'un içindeki
           `display: table` sarmalayıcı grid'in daralmasını engelliyor, uzun
           model adlarında kartlar taşıp kesiliyordu; dokunmatikte de kaydırma
           takılıyordu. `p-1` kart odak halkası kırpılmasın diye. */
        <div className="-mx-1 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4 p-1 sm:grid-cols-3 xl:grid-cols-4">
            {visible.map((option) => (
              <OptionCard
                key={option.id}
                option={option}
                selected={option.id === selectedId}
                onSelect={() => onSelect(option.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const OptionCard = ({
  option,
  selected,
  onSelect,
}: {
  option: TalepOption;
  selected: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    aria-pressed={selected}
    className={cn(
      'group flex cursor-pointer flex-col overflow-hidden rounded-2xl border text-left transition-all duration-200',
      'focus-visible:ring-primary/30 focus-visible:ring-2 focus-visible:outline-none',
      selected
        ? 'border-primary ring-primary/60 bg-primary/[0.03] ring-1'
        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
    )}
  >
    <span className="relative flex h-28 items-center justify-center p-4 xl:h-36 xl:p-5">
      {option.imageUrl ? (
        <Image
          src={option.imageUrl}
          alt={option.label}
          width={200}
          height={160}
          className="h-full w-auto object-contain"
        />
      ) : (
        <ImageOff className="size-6 text-gray-300" />
      )}

      {selected && (
        <span className="bg-primary absolute top-3 right-3 flex size-6 items-center justify-center rounded-full text-white">
          <Check className="size-3.5" strokeWidth={3} />
        </span>
      )}
    </span>

    <span className="flex items-center justify-between gap-2 px-3 py-3 xl:px-4 xl:py-3.5">
      <span className="min-w-0">
        <span className="block text-xs font-semibold break-words text-gray-900 sm:text-sm xl:text-base">
          {option.label}
        </span>
        {option.hint && (
          <span className="mt-0.5 block text-xs break-words text-gray-500 sm:text-sm">
            {option.hint}
          </span>
        )}
      </span>
      <span
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full transition-colors',
          selected ? 'bg-primary text-white' : 'bg-primary/10 text-primary'
        )}
      >
        <ArrowRight className="size-3.5" />
      </span>
    </span>
  </button>
);

export default OptionGrid;
